"use client";

import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import AutoBreadcrumbsFrame from "./AutoBreadcrumbsFrame";
import { rendersOwnBreadcrumb } from "./page-owned-breadcrumbs";
import { isHindiTrailPath, resolveTrailWith } from "@/lib/breadcrumbs-base";

// The Hindi resolver carries the Hindi name of every /hi/ route (several KB of
// Devanagari). Loading it only on /hi/ pages keeps it out of the first-load JS
// every English page downloads; the page still server-renders the Hindi trail,
// so nothing changes for a reader.
const HindiAutoBreadcrumbs = dynamic(() => import("./HindiAutoBreadcrumbs"));

/**
 * Auto-injects breadcrumbs on every page based on pathname.
 * Injected once in layout.tsx — no per-page changes needed.
 * Pages that should not show breadcrumbs are handled by the resolver returning null;
 * pages that render their own are listed in page-owned-breadcrumbs.ts.
 */
export default function AutoBreadcrumbs() {
  const pathname = usePathname();
  // Checked first, so a Hindi page with its own trail never loads the Hindi resolver.
  if (rendersOwnBreadcrumb(pathname)) return null;
  if (isHindiTrailPath(pathname)) return <HindiAutoBreadcrumbs pathname={pathname} />;
  // A non-Hindi path never reaches the Hindi resolver.
  const trail = resolveTrailWith(pathname, () => null);
  return trail ? <AutoBreadcrumbsFrame trail={trail} /> : null;
}
